import { Skeleton } from '@material-ui/lab';
import { DebounceComponent } from 'components/DebounceComponent';

export interface ISkeletonTreeProps {
  height: number;
  style?: React.CSSProperties;
  model?: never;
}

export interface ISkeletonTreeModelProps {
  height?: never;
  style?: React.CSSProperties;
  model: Map<string, number>;
}

const nodeHeight = 30;
const marginBottom = 4;
const marginHorizontal = 5;

/**
 * Skeleton loader for TreeView control
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function SkeletonTree({ height, style, model }: ISkeletonTreeProps | ISkeletonTreeModelProps) {
  const nodes = model ?? generateModel(height ?? 0);

  return <DebounceComponent>
    <div className="dp-skeleton-tree" style={style}>
      {Array.from(nodes.entries()).map(([key, level]) =>
        <Skeleton
          key={key}
          variant="rect"
          className="dp-skeleton-tree__node"
          height={nodeHeight}
          style={{
            marginLeft: marginHorizontal + 20 * level,
          }} />
      )}
    </div>
  </DebounceComponent>;
}

/**
 * Generates following tree structure. Every new iteration contains one more nested level.
 * ********** 
 *    **********
 * **********
 *    **********
 *       **********
 * **********
 *    **********
 *       **********
 *          **********
 * @param height height of skeleton
 * @returns model to render skeleton
 */
function generateModel(height: number): Map<string, number> {
  const nodesCount = Math.floor(height / (nodeHeight + marginBottom));
  const model = new Map<string, number>();

  if (nodesCount === 0 || isNaN(nodesCount)) {
    return model;
  }

  let addedNodes = 0;
  let iteration = 1;

  while (addedNodes < nodesCount) {
    for (let level = 0; level <= iteration && addedNodes < nodesCount; level++) {
      model.set(`${iteration}_${level}`, level);
      addedNodes++;
    }
    iteration++;
  }

  return model;
}
